import React, { useEffect, useMemo, useState } from 'react';
import { useRemote, useStorage } from '../../../Utils/Utils';
import { fetchAnalyticsConfig, saveAnalyticsConfig, deleteAnalyticsConfig } from '../../../Utils/AnalyticsConfigApi';
import { toastDanger, toastSuccess } from '../../../components/popups/Toast';
import defaultAnalyticsConfig from '../../Analytics/AnalyticsPortfolioLayout.json';
import Page, { PageContentBanner, PageContentBodyGrid } from '../../Shared/Page/Page';
import PageGridItem from '../../Shared/Page/PageGridItem';
import ActionButtonsBanner from '../../Shared/Banner/ActionButtonsBanner';
import Button from '../../../components/Buttons/Button';
import Divider from '../../../components/Divider';
import ButtonGroupWrapper from '../../../components/Buttons/ButtonGroupWrapper';
import { Trash2 } from 'react-feather';
import LoadingIcon from '../../../components/LoadingIcon';
import './AnalyticsConfigEditor.scss';

const AnalyticsConfigEditor = () => {
	const remote = useRemote();
	const storage = useStorage();
	const [configText, setConfigText] = useState('');
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [hasCustomConfig, setHasCustomConfig] = useState(false);
	const [previewTab, setPreviewTab] = useState(0);

	const parsed = useMemo(() => {
		try {
			const obj = JSON.parse(configText);
			if (obj && obj.mainTab && obj.tabs) return obj;
		} catch (e) { /* ignore */ }
		return null;
	}, [configText]);

	useEffect(() => {
		loadConfig();
	}, []);

	useEffect(() => {
		setPreviewTab(0);
	}, [parsed?.tabs?.length]);

	const loadConfig = () => {
		setLoading(true);
		fetchAnalyticsConfig(remote)
			.then((data) => {
				if (data && data.config !== null && data.mainTab) {
					setConfigText(JSON.stringify(data, null, 2));
					setHasCustomConfig(true);
				} else {
					setConfigText('');
					setHasCustomConfig(false);
				}
			})
			.catch(() => {
				setConfigText('');
				setHasCustomConfig(false);
			})
			.finally(() => setLoading(false));
	};

	const handleSave = () => {
		if (!parsed) {
			toastDanger('Invalid JSON or missing required keys (mainTab, tabs)');
			return;
		}

		setSaving(true);
		saveAnalyticsConfig(remote, parsed)
			.then(() => {
				toastSuccess('Analytics config saved');
				setHasCustomConfig(true);
				storage.refresh("crm/analytics-config");
			})
			.catch(() => toastDanger('Failed to save analytics config'))
			.finally(() => setSaving(false));
	};

	const handleDelete = () => {
		setSaving(true);
		deleteAnalyticsConfig(remote)
			.then(() => {
				toastSuccess('Analytics config deleted — default will be used');
				setConfigText('');
				setHasCustomConfig(false);
				storage.refresh("crm/analytics-config");
			})
			.catch(() => toastDanger('Failed to delete analytics config'))
			.finally(() => setSaving(false));
	};

	if (loading) {
		return <LoadingIcon />;
	}

	return (
		<Page>
			<PageContentBanner divider>
				<ActionButtonsBanner
					hideBackButton
					editMode={true}
					save={{
						label: saving ? "Saving..." : "Save",
						disabled: saving || !configText || !parsed,
						onClick: handleSave
					}}
					customButton={hasCustomConfig ? (
						<ButtonGroupWrapper>
							<Divider vertical />
							<Button icon={<Trash2 />} variant="secondary" hover="danger" onClick={handleDelete} disabled={saving}>
								Delete
							</Button>
						</ButtonGroupWrapper>
					) : undefined}
				/>
			</PageContentBanner>
			<PageContentBodyGrid splitScroll>
				<PageGridItem col="1 / span 6" className="analytics-config-editor-pane">
					<textarea
						className="analytics-config-textarea"
						value={configText}
						onChange={(e) => setConfigText(e.target.value)}
						placeholder='Paste analytics config JSON here...'
						spellCheck={false}
					/>
				</PageGridItem>
				<PageGridItem col="7 / span 6" className="analytics-config-preview-pane">
					{parsed ? (
						<ConfigPreview config={parsed} activeTab={previewTab} setActiveTab={setPreviewTab} />
					) : configText ? (
						<div className="analytics-config-preview-empty">Invalid JSON — fix errors to see preview</div>
					) : (
						<>
							<div className="analytics-config-preview-default-label">Default layout preview</div>
							<ConfigPreview config={defaultAnalyticsConfig} activeTab={previewTab} setActiveTab={setPreviewTab} />
						</>
					)}
				</PageGridItem>
			</PageContentBodyGrid>
		</Page>
	);
};

const ConfigPreview = ({ config, activeTab, setActiveTab }) => {
	const { mainTab, tabs } = config;

	const activeTabData = activeTab === 0 ? mainTab : (tabs[activeTab] || null);
	const hasGrid = activeTab === 0 && mainTab.components;

	return (
		<div className="config-preview">
			<div className="config-preview-tabs">
				{tabs.map((tab, i) => (
					<div
						key={i}
						className={`config-preview-tab${activeTab === i ? ' active' : ''}`}
						onClick={() => setActiveTab(i)}
					>
						{tab.name}
					</div>
				))}
			</div>

			{hasGrid ? (
				<DashboardPreview mainTab={mainTab} />
			) : activeTabData ? (
				<div className="config-preview-tab-info">
					<div className="config-preview-label">Path</div>
					<div className="config-preview-value">{activeTabData.path}</div>
					{activeTabData.specialComponent && (
						<>
							<div className="config-preview-label">Component</div>
							<div className="config-preview-value">{activeTabData.specialComponent}</div>
						</>
					)}
					{activeTabData.listData && (
						<>
							<div className="config-preview-label">List headers</div>
							<div className="config-preview-value">
								{activeTabData.listData.headers?.map(h => h.label || h.column).join(', ')}
							</div>
						</>
					)}
				</div>
			) : null}
		</div>
	);
};

const DashboardPreview = ({ mainTab }) => {
	const columns = mainTab.gridTemplateColumns || '1fr 1px 1fr';
	const left = mainTab.components?.left || [];
	const right = mainTab.components?.right || [];

	return (
		<div className="config-preview-dashboard">
			<div className="config-preview-grid" style={{ gridTemplateColumns: columns }}>
				<div className="config-preview-column">
					{left.map((comp, i) => <ComponentBlock key={i} comp={comp} />)}
				</div>
				<div className="config-preview-divider" />
				<div className="config-preview-column">
					{right.map((comp, i) => <ComponentBlock key={i} comp={comp} />)}
				</div>
			</div>
		</div>
	);
};

const ComponentBlock = ({ comp }) => {
	if (comp.sideBySide && comp.components) {
		return (
			<div className="config-preview-side-by-side">
				{comp.components.map((c, i) => (
					<div key={i} className="config-preview-block">
						<span className="config-preview-block-name">{c.name || 'unnamed'}</span>
					</div>
				))}
			</div>
		);
	}
	if (comp.toggable && comp.components) {
		return (
			<div className="config-preview-block toggable">
				<span className="config-preview-block-name">
					{comp.components.map(c => c.name).join(' / ')}
				</span>
				<span className="config-preview-block-badge">toggle</span>
			</div>
		);
	}
	return (
		<div className="config-preview-block">
			<span className="config-preview-block-name">{comp.name || 'unnamed'}</span>
			{comp.showIf && <span className="config-preview-block-badge">if: {comp.showIf}</span>}
		</div>
	);
};

export default AnalyticsConfigEditor;
